import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { NavLink, useLocation } from 'react-router-dom';
import { Briefcase, User, Home, Menu, X, Search } from 'lucide-react';

export const Navigation: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const prefersReduced = useReducedMotion();

  // Close the dropdown and return keyboard focus to the toggle that opened it.
  const closeMenu = () => {
    setMobileMenuOpen(false);
    requestAnimationFrame(() => toggleRef.current?.focus());
  };

  // Handle scroll effect — hysteresis dead-band (enter >24, exit <8) stops the
  // `scrolled` flag from flip-flopping near the boundary and reversing the
  // in-flight 500ms shrink transition. rAF-throttled + passive listener.
  useEffect(() => {
    let ticking = false;
    const evaluate = () => {
      ticking = false;
      const y = window.scrollY;
      setScrolled((prev) => (y > 24 ? true : y < 8 ? false : prev));
    };
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(evaluate);
    };
    handleScroll(); // sync state on mount (e.g. reload while already scrolled)
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Dismiss the dropdown on Escape, on any page scroll, and on an outside tap.
  // A compact dropdown dismisses on scroll (like a native menu), which also
  // stops the navbar's shrink transition from fighting an open panel. No
  // body-scroll lock is used: the panel is small and the page stays
  // visible/interactive behind it.
  //
  // Outside-tap dismissal uses a document `pointerdown` listener instead of a
  // full-viewport fixed click-catcher. The old `fixed inset-0` catcher spanned
  // the whole visual viewport — including the bottom safe-area behind iOS 26
  // Safari's floating "Liquid Glass" bar (index.html has viewport-fit=cover).
  // That full-viewport fixed compositing layer forced the bar opaque grey, and
  // a WebKit teardown quirk kept it grey until reload. A listener leaves the DOM
  // with no full-screen layer, so the bar has nothing to opacify over.
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu();
    };
    const onScroll = () => setMobileMenuOpen(false);
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      // Taps on the panel itself or the hamburger toggle are handled by their
      // own handlers — only close when the tap lands truly outside both.
      if (panelRef.current?.contains(target)) return;
      if (toggleRef.current?.contains(target)) return;
      setMobileMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [mobileMenuOpen]);

  const navItems = [
    { name: 'Home', path: '/', icon: Home },
    { name: 'Portfolio', path: '/portfolio', icon: Briefcase },
    { name: 'About', path: '/about', icon: User },
  ];

  const openSearch = () => window.dispatchEvent(new Event('open-command-palette'));
  const openContact = () => window.dispatchEvent(new Event('open-contact-modal'));

  // GPU-only motion (opacity + transform). The panel unfolds straight DOWN from
  // the top edge — a vertical slide plus a whisper of scaleY anchored at the top
  // (transform-origin: top), never a corner unfold. Enter and exit carry their
  // own transitions: a long expo-out settle on open (opacity leads so nothing
  // flashes late), a crisper accelerate-in on close so it retracts cleanly
  // instead of snapping. Reduced motion → plain crossfade.
  const easeOut = [0.16, 1, 0.3, 1] as [number, number, number, number]; // expo-out
  const easeIn = [0.4, 0, 0.2, 1] as [number, number, number, number]; // accelerate-in
  const panelMotion = prefersReduced
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1, transition: { duration: 0.15 } },
        exit: { opacity: 0, transition: { duration: 0.12 } },
      }
    : {
        initial: { opacity: 0, y: -10, scaleY: 0.96 },
        animate: {
          opacity: 1,
          y: 0,
          scaleY: 1,
          transition: {
            duration: 0.3,
            ease: easeOut,
            opacity: { duration: 0.22, ease: 'easeOut' },
          },
        },
        exit: {
          opacity: 0,
          y: -10,
          scaleY: 0.97,
          transition: {
            duration: 0.2,
            ease: easeIn,
            opacity: { duration: 0.16 },
          },
        },
      };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-in-out ${scrolled ? 'py-4' : 'py-8'}`}>
        <div className="container mx-auto px-4 flex justify-center">
          {/* RELATIVE ANCHOR — the mobile dropdown hangs off this via `top-full`,
              so it auto-tracks the navbar in both scroll states (py-8 rest /
              py-4 scrolled) and inherits the pill's width. No hardcoded offset. */}
          <div className="relative w-full flex justify-center">
          <div
            className={`
              flex items-center justify-between rounded-full
              backdrop-blur-xl border shadow-xl md:shadow-2xl
              transition-all duration-500 ease-in-out
              relative z-50 mx-auto box-border
              
              bg-white/80 border-white/40
              dark:bg-[#121212]/80 dark:border-white/10
              [.nardogrey_&]:bg-white/10 [.nardogrey_&]:border-white/20

              /* АНИМАЦИЯ СЖАТИЯ */
              ${scrolled
                ? 'w-full max-w-[900px] px-6 py-3 shadow-black/20'
                : 'w-full max-w-7xl px-8 py-5 shadow-none border-transparent bg-transparent dark:bg-transparent'}
            `}
          >
            {/* LOGO */}
            <NavLink 
              to="/" 
              className={`
                font-bold tracking-[0.2em] text-zinc-900 dark:text-white hover:opacity-80 uppercase whitespace-nowrap
                inline-block origin-left will-change-transform transition-transform duration-500 ease-in-out
                text-sm md:text-lg
                ${scrolled ? 'scale-[0.86] md:scale-[0.78]' : 'scale-100'}
              `}
            >
              HELENENDORFSKY
            </NavLink>

            {/* DESKTOP NAV — a real <ul> so each link sits in its own block
                element: CSS-blind text extractors then see word boundaries
                between links instead of "HomePortfolioAbout". Preflight zeroes
                list margins/padding, so rendering is unchanged. */}
            <div className="hidden md:flex items-center gap-1 flex-shrink-0 transition-all duration-300">
              <ul className="flex items-center gap-1 list-none m-0 p-0">
                {navItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <li key={item.path}>
                      <NavLink
                        to={item.path}
                        className={`
                          relative px-4 py-2 rounded-full text-[11px] font-bold transition-all duration-300 uppercase tracking-widest
                          ${isActive
                            ? 'text-zinc-900 dark:text-white bg-black/5 dark:bg-white/10'
                            : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5'}
                        `}
                      >
                        {item.name}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>

              <div className={`w-px h-6 bg-zinc-300 dark:bg-white/10 mx-2 ${scrolled ? 'hidden lg:block' : 'block'}`} />

              {/* SEARCH — bare icon at rest (no resting circle/bg), subtle hover */}
              <button
                onClick={openSearch}
                aria-label="Search"
                className={`
                  group flex items-center justify-center p-2 rounded-full
                  hover:bg-black/10 dark:hover:bg-white/10
                  border border-transparent hover:border-black/5 dark:hover:border-white/5
                  text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-200 transition-all
                `}
              >
                <Search size={16} />
              </button>

              {/* CONTACT BUTTON — font-size held stable (text-xs) so only padding
                  animates; duration-500 ease-in-out resizes in lockstep with the
                  navbar pill instead of snapping. */}
              <button
                onClick={openContact}
                className={`
                  ml-2 hidden lg:flex rounded-full bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 font-bold text-xs hover:opacity-90 transition-all duration-500 ease-in-out shadow-lg
                  ${scrolled ? 'px-4 py-1.5' : 'px-5 py-2'}
                `}
              >
                Contact
              </button>
            </div>

            {/* MOBILE TOGGLE */}
            <div className="flex items-center gap-3 md:hidden">
              <button onClick={openSearch} aria-label="Search" className="text-zinc-500 dark:text-zinc-400">
                <Search size={20} />
              </button>
              <button
                ref={toggleRef}
                className="text-zinc-900 dark:text-white p-1"
                onClick={() => setMobileMenuOpen((v) => !v)}
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-menu"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

            {/* MOBILE MENU — outside taps are caught by a document `pointerdown`
                listener (see effect above), NOT a full-viewport fixed overlay,
                so iOS Safari's floating bottom bar never opacifies. The page
                stays visible/interactive behind the panel. */}

            {/* MOBILE DROPDOWN — compact frosted card anchored under the pill. */}
            <AnimatePresence>
              {mobileMenuOpen && (
                <motion.div
                  key="mobile-menu-panel"
                  ref={panelRef}
                  id="mobile-menu"
                  role="menu"
                  aria-label="Mobile navigation"
                  {...panelMotion}
                  style={{ transformOrigin: 'top' }}
                  className="
                    absolute top-full left-0 right-0 mt-3 z-50 md:hidden
                    rounded-[20px] p-2 backdrop-blur-2xl
                    shadow-[0_18px_48px_-16px_rgba(0,0,0,0.55)]
                    bg-white/85 border border-black/10
                    dark:bg-[#141414]/85 dark:border-white/10
                    [.nardogrey_&]:bg-white/10 [.nardogrey_&]:border-white/15
                  "
                >
                  <div className="flex flex-col gap-0.5">
                    {navItems.map((item) => (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        role="menuitem"
                        onClick={() => setMobileMenuOpen(false)}
                        className={({ isActive }) => `
                          block w-full rounded-xl px-4 py-3 text-[13px] uppercase tracking-[0.18em] transition-colors duration-200
                          ${isActive
                            ? 'bg-black/[0.06] text-zinc-900 font-semibold dark:bg-white/10 dark:text-white [.nardogrey_&]:bg-white/10 [.nardogrey_&]:text-white'
                            : 'font-medium text-zinc-600 hover:bg-black/[0.04] hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-white/[0.06] dark:hover:text-white [.nardogrey_&]:text-white/60 [.nardogrey_&]:hover:bg-white/[0.06] [.nardogrey_&]:hover:text-white'}
                        `}
                      >
                        {item.name}
                      </NavLink>
                    ))}
                  </div>

                  {/* HAIRLINE DIVIDER — balanced spacing above / below. */}
                  <div className="mx-2 my-1.5 h-px bg-black/10 dark:bg-white/10 [.nardogrey_&]:bg-white/10" />

                  {/* CONTACT — primary CTA pill at the bottom of the card. */}
                  <button
                    onClick={() => { setMobileMenuOpen(false); openContact(); }}
                    role="menuitem"
                    className="
                      w-full rounded-xl px-4 py-3 text-[13px] font-semibold uppercase tracking-[0.18em]
                      bg-zinc-900 text-white
                      dark:bg-white dark:text-zinc-900
                      [.nardogrey_&]:bg-white [.nardogrey_&]:text-zinc-900
                      shadow-[0_6px_20px_-6px_rgba(0,0,0,0.45)]
                      transition-all duration-200 will-change-transform
                      hover:-translate-y-px hover:shadow-[0_10px_28px_-8px_rgba(0,0,0,0.55)]
                      [.nardogrey_&]:hover:bg-white/90
                      active:translate-y-0
                    "
                  >
                    Contact
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </nav>
    </>
  );
};
